import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtIDR } from "@/lib/constants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { cn } from "@/lib/utils";

const Stat = ({ label, value, color }) => (
  <div className="card-lux p-4">
    <p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">{label}</p>
    <p className={cn("font-heading text-2xl mt-1 leading-none", color || "text-[#1A1A1A]")}>{value}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-heading text-2xl text-burgundy mb-3">{title}</h2>
    {children}
  </div>
);

const PIE_COLORS = ["#800020", "#D4AF37", "#A23B52", "#997A00", "#5C0015"];

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-[#7A7A7A]">Memuat laporan...</div>;

  const er = data.event_report, fr = data.financial_report, cr = data.client_report;

  return (
    <div>
      <PageHeader overline="Analitik & Laporan" title="Reports" subtitle="Ringkasan performa event, keuangan, client, vendor, dan tim." />

      <Section title="Event Report">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Event" value={er.total} color="text-burgundy" />
          <Stat label="Completed" value={er.completed} color="text-emerald-600" />
          <Stat label="Upcoming" value={er.upcoming} color="text-blue-600" />
          <Stat label="Cancelled" value={er.cancelled} color="text-gray-500" />
        </div>
      </Section>

      <Section title="Financial Report">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Revenue" value={fmtIDR(fr.revenue)} />
          <Stat label="Paid" value={fmtIDR(fr.paid)} color="text-emerald-600" />
          <Stat label="Outstanding" value={fmtIDR(fr.outstanding)} color="text-amber-600" />
          <Stat label="Est. Profit" value={fmtIDR(fr.profit)} color="text-burgundy" />
        </div>
      </Section>

      <Section title="Client Report">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="New Clients" value={cr.new} />
          <Stat label="Booked" value={cr.booked} color="text-emerald-600" />
          <Stat label="Completed" value={cr.completed} color="text-blue-600" />
          <Stat label="Conversion" value={`${cr.conversion_rate}%`} color="text-burgundy" />
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Vendor Usage">
          <div className="card-lux p-4">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.vendor_report} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="usage" fill="#800020" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Team Performance">
          <div className="card-lux p-4 space-y-3">
            {data.team_report.map((t) => (
              <div key={t.pic} className="flex items-center justify-between text-sm border-b border-gold/10 pb-2 last:border-0">
                <span className="font-medium text-[#1A1A1A]">{t.pic}</span>
                <span className="flex gap-3 text-xs">
                  <span className="text-emerald-600">{t.done} done</span>
                  <span className="text-red-600">{t.overdue} overdue</span>
                  <span className="text-[#7A7A7A]">{t.total} total</span>
                </span>
              </div>
            ))}
            {data.team_report.length === 0 && <p className="text-sm text-[#7A7A7A] text-center py-4">Belum ada data.</p>}
          </div>
        </Section>
      </div>
    </div>
  );
}
